import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { Modulos } from "@/app/navegacao";
import { useQuery } from "@tanstack/react-query";
import { buscarProgresso } from "@/features/onboarding/api/onboarding";
import { useSessao } from "@/app/AuthContext";
import { Carregando } from "@/components/layout/Carregando";

/**
 * Ordem do guard: sessao, empresa e (na fase 9) billing.
 * Usuario logado sem empresa vai para o onboarding em vez de ver o app vazio.
 */
export function RotaProtegida() {
  const sessao = useSessao();
  const local = useLocation();

  if (sessao.situacao === "carregando") return <Carregando />;
  if (sessao.situacao === "deslogado") {
    return <Navigate to="/entrar" replace state={{ de: local.pathname }} />;
  }
  if (sessao.situacao === "sem_empresa") return <Navigate to="/comecar" replace />;

  return <Outlet />;
}

/**
 * Leva ao guia quem ainda nao terminou o onboarding.
 *
 * So dono e admin: atendente convidado nao configura nada, e cair num tour de
 * configuracao que ele nao pode concluir seria uma parede.
 */
export function GuardaOnboarding() {
  const sessao = useSessao();
  const local = useLocation();

  const { data: progresso } = useQuery({
    queryKey: ["onboarding"],
    queryFn: buscarProgresso,
    enabled: sessao.situacao === "pronto" && sessao.membro.papel !== "atendente",
    staleTime: 30_000,
  });

  if (sessao.situacao !== "pronto") return <Outlet />;
  if (sessao.membro.papel === "atendente") return <Outlet />;
  if (local.pathname.startsWith("/bem-vindo")) return <Outlet />;
  // Os passos do guia apontam para as telas de configuracao. Barrar aqui
  // devolvia o cliente ao guia no mesmo instante: o "Conectar" so piscava.
  if (local.pathname.startsWith("/configuracoes")) return <Outlet />;

  // Sem resposta ainda: nao segura a tela. Melhor mostrar o app e redirecionar
  // depois do que piscar um carregando a cada navegacao.
  if (progresso && !progresso.concluido) {
    return <Navigate to="/bem-vindo" replace />;
  }

  return <Outlet />;
}

/**
 * Rota de gestao: so dono e admin.
 *
 * O menu ja nao mostra esses itens para o atendente, mas esconder link nao e
 * controle de acesso: sem este guard, ele chega digitando a URL. A defesa de
 * verdade esta na RLS e nas funcoes do banco; isto aqui e para ele nao ver uma
 * tela quebrada.
 */
export function RotaDeGestao() {
  const sessao = useSessao();

  if (sessao.situacao === "carregando") return <Carregando />;
  if (sessao.situacao !== "pronto") return <Navigate to="/entrar" replace />;
  if (sessao.membro.papel === "atendente") return <Navigate to="/atendimento" replace />;

  return <Outlet />;
}

/** Modulo desligado: a rota some junto com o item do menu. */
export function RotaDeModulo({ modulo }: { modulo: keyof Modulos }) {
  const sessao = useSessao();
  if (sessao.situacao === "pronto" && !sessao.membro.modulos[modulo]) {
    return <Navigate to="/atendimento" replace />;
  }
  return <Outlet />;
}
